/**
 * ISSUE-1039. Where a pool search result leads, and how a `/pool/join-preview`
 * answer by `targetPoolId` becomes the search join dialog. Pure, no React.
 *
 * The two pending strings repeat `JoinPoolDialog.tsx` word for word on
 * purpose: a fan who joins by code and a fan who joins from search should
 * read the same sentence, and the code flow is not edited by this card.
 */
import { joinPoolErrorCopy } from "./joinPoolErrorCopy";
import {
  resolveJoinAvailability,
  type typeJoinAvailability,
} from "./joinPoolFlow";
import type { typeJoinPoolPreviewModel } from "./JoinPoolPreview";
import type { typePoolSearchResultModel } from "./poolSearchState";

export const POOL_SEARCH_JOIN_DIALOG = "join-pool";
export const POOL_SEARCH_JOIN_TITLE = "Join a pool";
export const POOL_SEARCH_JOIN_BACK_LABEL = "Back to search";
export const POOL_SEARCH_JOIN_FULL_NOTICE = "This pool is full right now.";
export const POOL_SEARCH_JOIN_UNAVAILABLE_COPY =
  "This pool isn't open to join anymore. Pick another pool from your search.";
export const POOL_SEARCH_JOIN_PENDING_HEADING = "You're on the list";
export const POOL_SEARCH_JOIN_PENDING_MESSAGE =
  "The Commissioner has to approve you before you can play. We'll let you know.";

export type typePoolSearchSelection =
  | { kind: "invites" }
  | { kind: "openPool"; poolId: string }
  | { kind: "joinPreview"; poolId: string };

export type typePoolSearchJoinView = {
  model: typeJoinPoolPreviewModel;
  availability: typeJoinAvailability;
  isFull: boolean;
};

/** The subset of `/pool/join-preview`'s 200 `data` this module reads. */
export type typePoolSearchJoinPreviewData = {
  pool: {
    poolId: string;
    name: string;
    leagueKey: string;
    contestType: string;
    activeMemberCount: number;
    isFull: boolean;
  };
  rulesSummary: string;
  isAlreadyMember: boolean;
  membershipStatus?:
    "pending" | "active" | "suspended" | "left" | "removed" | "rejected" | null;
};

export type typePoolSearchJoinPreviewOutcome =
  | { kind: "goToPool"; poolId: string }
  | { kind: "preview"; view: typePoolSearchJoinView };

// Never reads `isMember`: `/pool/search` sets it for pending and suspended members too, so it cannot say the pool will open.
export function resolvePoolSearchSelection(
  result: typePoolSearchResultModel,
): typePoolSearchSelection {
  if (result.hasPendingInvite) return { kind: "invites" };
  if (result.isPrivate) return { kind: "openPool", poolId: result.poolId };
  return { kind: "joinPreview", poolId: result.poolId };
}

export function readPoolSearchJoinPreview(
  data: typePoolSearchJoinPreviewData,
): typePoolSearchJoinPreviewOutcome {
  const status =
    data.membershipStatus ?? (data.isAlreadyMember ? "active" : null);
  if (status === "active") {
    return { kind: "goToPool", poolId: data.pool.poolId };
  }
  return {
    kind: "preview",
    view: {
      model: {
        poolId: data.pool.poolId,
        name: data.pool.name,
        leagueKey: data.pool.leagueKey,
        contestType: data.pool.contestType,
        activeMemberCount: data.pool.activeMemberCount,
        rulesSummary: data.rulesSummary,
      },
      availability: resolveJoinAvailability({
        isAlreadyMember: data.isAlreadyMember,
        membershipStatus: data.membershipStatus ?? null,
      }),
      isFull: data.pool.isFull,
    },
  };
}

/**
 * A refused preview by `targetPoolId` means the pool went private, was
 * archived, or is gone. The code flow's "check the invite code" sentence
 * would be wrong here, since the fan typed no code.
 */
export function poolSearchJoinPreviewErrorCopy(
  code: string | null | undefined,
): string {
  if (code === "POOL_CODE_NOT_FOUND" || code === "VALIDATION_ERROR") {
    return POOL_SEARCH_JOIN_UNAVAILABLE_COPY;
  }
  return joinPoolErrorCopy(null);
}
